<template>
  <div class="invite-section-wrap">
    <div
      class="invite-section-content"
      :class="{ 'border-b': inviteeItems.length }"
    >
      <div class="invite-section-header">
        <em
          class="custom-icon invite-section-header-icon"
          :style="getCustomIconStyle(iconPath('user-plus'))"
        />
        <span class="invite-section-text">
          {{ translate('inviteNewPeople') }}
        </span>
      </div>
      <div class="invite-section-input-row">
        <StInput
          :ref="'emailAddressRef'"
          :key="'email-address'"
          :value="emailAddress"
          class="invite-section-email-input"
          :text-value="emailAddress"
          :label-text="''"
          :placeholder-text="translate('inviteeEmailAddress')"
          :input-type="'text'"
          :input-height="44"
          :custom-icon="'mail'"
          :error-message="emailInputErrorMessage"
          @input="emailAddress = $event"
          @enter-key="inviteByEmail"
          @validate="emailValidation"
        />
        <ButtonCustom
          class="invite-section-button"
          :type="'important'"
          :text="translate('add')"
          @click="inviteByEmail"
        />
      </div>
      <span class="invite-section-subtitle-text">
        {{ translate('inviteSectionSubtitle') }}
      </span>
    </div>
    <vue-scroll
      v-if="inviteeItems.length"
      :ops="vueScrollOps"
    >
      <div class="invitees-by-email-list">
        <FilterDropdownItem
          v-for="(item, index) in inviteeItems"
          :key="index"
          class="invitee-by-email-list-item"
          :index="index"
          :item="item"
          :items="inviteeItems"
          :selected-item-index="selectedIndex"
          :hover-preview-disabled="false"
          @item-click="leftSideClick(item)"
          @item-hover="setSelectedIndex(index)"
          @item-right-side-click="rightSideClick(item.id)"
        />
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import ButtonCustom from '@/components/elements/ButtonCustom';
import StInput from '@/components/elements/StInput';
import FilterDropdownItem from '@/components/widgets/filters/FilterDropdownItem';
import customIconsMixin from '@/mixins/customIconsMixin';
import inputDropdown from '@/mixins/inputDropdown';
import validateEmail from '@/utilities/validateEmail';

export default {
  name: 'MembersDropdownInviteSection',
  components: {
    ButtonCustom,
    StInput,
    FilterDropdownItem,
  },
  mixins: [customIconsMixin, inputDropdown],
  props: {},
  emits: ['handleEsc'],
  data() {
    return {
      emailAddress: '',
      inviteeItems: [],
      vueScrollOps: {
        scrollPanel: { scrollingX: false },
        rail: { gutterOfSide: '6px', gutterOfEnds: '8px' },
        bar: { size: '4px' },
      },
    };
  },
  computed: {
    emailInputErrorMessage() {
      return this.emailAlreadyAdded
        ? this.translate('emailAlreadyAdded')
        : this.translate('validEmail');
    },
    emailAlreadyAdded() {
      return (
        this.$store.getters.getMembersDropdownInvitees.findIndex(
          (i) => i.email === this.emailAddress
        ) > -1
      );
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
    this.inviteeItems = this.generateInviteeItems();
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('handleEsc', e);
      }
    },
    setSelectedIndex(index) {
      this.selectedIndex = index;
    },
    generateInviteeItems() {
      const invitees = this.$store.getters.getMembersDropdownInvitees.filter(
        (invitee) => !invitee.id
      );

      return invitees.map((inv) => ({
        icon: 'mail',
        customIcon: true,
        id: inv.email,
        lighter: true,
        checkbox: true,
        checked: true,
        label: inv.email,
        isInvitee: true,
      }));
    },
    inviteByEmail() {
      if (this.emailValidation()) {
        return;
      }

      this.addItem();
      this.$store.dispatch('addMembersDropdownInvitee', { email: this.emailAddress });
      this.emailAddress = '';
      this.$refs.emailAddressRef.focusInput();
    },
    addItem() {
      const preexistingItemIndex = this.inviteeItems.findIndex((i) => i.id === this.emailAddress);
      if (preexistingItemIndex > -1) {
        this.inviteeItems[preexistingItemIndex].checked = true;
        return;
      }

      this.inviteeItems.push({
        icon: 'mail',
        customIcon: true,
        id: this.emailAddress,
        lighter: true,
        checkbox: true,
        checked: true,
        label: this.emailAddress,
        isInvitee: true,
      });
    },
    checkItem(invitee) {
      const index = this.inviteeItems.findIndex((i) => i.id === invitee.id);
      this.inviteeItems[index].checked = true;
      this.addInvitee(invitee.id);
    },
    uncheckItem(invitee) {
      const index = this.inviteeItems.findIndex((i) => i.id === invitee.id);
      this.inviteeItems[index].checked = false;
      this.removeInvitee(invitee.id);
    },
    leftSideClick(item) {
      if (item.checked) {
        this.uncheckItem(item);
      } else {
        this.checkItem(item);
      }
    },
    rightSideClick(email) {
      this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
      this.removeInvitee(email);
    },
    addInvitee(email) {
      this.$store.dispatch('addMembersDropdownInvitee', { email });
    },
    removeInvitee(email) {
      this.$store.dispatch('removeMembersDropdownInvitee', { email });
    },
    emailValidation() {
      const value = validateEmail(this.emailAddress);
      this.$refs.emailAddressRef.handleValidation(!value || this.emailAlreadyAdded);
      return !value || this.emailAlreadyAdded;
    },
  },
};
</script>

<style lang="scss">
.invite-section-wrap {
  .invite-section-text {
    @apply text-text-default text-sm;
  }

  .invite-section-content {
    @apply flex flex-col gap-y-2 py-[10px] px-4 border-solid border-0 border-t border-standard-border;

    .invite-section-header {
      @apply flex items-center gap-x-2;

      .invite-section-header-icon {
        @apply h-4 w-4 bg-icon-default;
      }
    }

    .invite-section-input-row {
      @apply flex flex-row items-baseline gap-x-2;

      .input-wrap {
        @apply h-fit flex grow;

        .input-field {
          @apply border-standard-border focus:border-standard-borderFocused bg-standard-bg text-text-default;
        }

        .input-error-icon {
          @apply top-[10px];
        }

        .st-input-custom-icon {
          @apply top-3;
        }

        .input-field-label {
          @apply hidden;
        }
      }

      .invite-section-button {
        @apply w-fit h-11 px-[18px];

        .button-label {
          @apply text-sm;
        }
      }
    }
  }

  .invite-section-subtitle-text {
    @apply text-deEmphasised-text text-sm;
  }

  .invitees-by-email-list {
    @apply flex flex-col border-standard-border h-full max-h-[200px];
  }

  .__panel {
    @apply pb-1;
  }
}
</style>
